import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { I18nProvider } from './contexts/I18nContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import DashboardPage from './pages/DashboardPage';
import ClientDashboardPage from './pages/ClientDashboardPage';
import ShipmentsPage from './pages/ShipmentsPage';
import ShipmentDetailPage from './pages/ShipmentDetailPage';
import NewShipmentPage from './pages/NewShipmentPage';
import ClientsPage from './pages/ClientsPage';
import ImportersPage from './pages/ImportersPage';
import SuppliersPage from './pages/SuppliersPage';
import CatalogsPage from './pages/CatalogsPage';
import UsersPage from './pages/UsersPage';
import OrganizationsPage from './pages/OrganizationsPage';
import PaymentsPage from './pages/PaymentsPage';
import ClientPaymentsPage from './pages/ClientPaymentsPage';
import ArnulfoPage from './pages/ArnulfoPage';
import AlertsPage from './pages/AlertsPage';
import AuditPage from './pages/AuditPage';
import CertificatesPage from './pages/CertificatesPage';
import PricingPage from './pages/PricingPage';
import TrackingPage from './pages/TrackingPage';
import EmailSettingsPage from './pages/EmailSettingsPage';
import ReportsPage from './pages/ReportsPage';
import ManualPage from './pages/ManualPage';
import AIArancelPage from './pages/AIArancelPage';
import SlaPage from './pages/SlaPage';
import SuperAdminPage from './pages/SuperAdminPage';
import AuditLogsPage from './pages/AuditLogsPage';
import PublicTrackingPage from './pages/PublicTrackingPage';
import OperationsByPartyPage from './pages/OperationsByPartyPage';
import { LanguageWelcomeModal } from './components/LanguageWelcomeModal';

import './App.css';

// Access denied screen - shown when user doesn't have permission to view a module
function AccessDenied() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-4 border border-border/60 rounded-lg p-8 bg-card" data-testid="access-denied-screen">
        <div className="text-4xl">🔒</div>
        <h1 className="text-2xl font-semibold">Acceso restringido</h1>
        <p className="text-muted-foreground text-sm">
          No tienes permiso para ver esta sección. Si crees que deberías tener acceso, contacta al administrador de tu organización.
        </p>
        <button
          onClick={() => { window.location.href = '/'; }}
          className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 transition-colors"
          data-testid="access-denied-home-btn"
        >
          Volver al inicio
        </button>
      </div>
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute({ children, allowedRoles, requireSuperAdmin, module, field, feature }) {
  const { user, loading, hasModule, hasField, hasFeature } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Super Admin restricted route: requires is_super_admin OR is_master_admin flag
  if (requireSuperAdmin) {
    if (user.is_super_admin === true || user.is_master_admin === true) {
      return children;
    }
    return <Navigate to="/shipments" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Super admin can access everything
    if (user.is_super_admin || user.role === 'super_admin') {
      return children;
    }
    return <Navigate to="/shipments" replace />;
  }

  // Per-org feature gating (e.g. client_arnulfo). OFIMEX internal staff
  // (super/master admin) always bypass this check.
  if (feature) {
    const isInternal = user.is_super_admin === true || user.is_master_admin === true;
    if (!isInternal && !hasFeature(feature)) {
      return <Navigate to="/shipments" replace />;
    }
  }

  // Per-user module permission check (admins always pass inside hasModule)
  if (module && !hasModule(module)) {
    return <AccessDenied />;
  }

  // Per-user field permission check (e.g. view_prices, view_profit, view_costs)
  if (field && !hasField(field)) {
    return <AccessDenied />;
  }

  return children;
}

// Main App Routes
function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      {/* Public tracking link — shared externally, NO auth */}
      <Route path="/track/:token" element={<PublicTrackingPage />} />
      {/* Register redirects to landing page */}
      <Route path="/register" element={<Navigate to="/" replace />} />

      {/* Dashboard - admin/ops only */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin', 'ops']} module="dashboard">
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Client Dashboard */}
      <Route
        path="/my-dashboard"
        element={
          <ProtectedRoute allowedRoles={['client']} module="dashboard">
            <ClientDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Shipments - all roles */}
      <Route
        path="/shipments"
        element={
          <ProtectedRoute module="shipments">
            <ShipmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipments/new"
        element={
          <ProtectedRoute allowedRoles={['admin', 'ops']} module="shipments">
            <NewShipmentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipments/:mbl"
        element={
          <ProtectedRoute module="shipments">
            <ShipmentDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Clients - admin only */}
      <Route
        path="/clients"
        element={
          <ProtectedRoute allowedRoles={['admin']} module="clients">
            <ClientsPage />
          </ProtectedRoute>
        }
      />

      {/* Importers - admin/ops */}
      <Route
        path="/importers"
        element={
          <ProtectedRoute allowedRoles={['admin', 'ops']} module="importers">
            <ImportersPage />
          </ProtectedRoute>
        }
      />

      {/* Suppliers - admin/ops */}
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute allowedRoles={['admin', 'ops']} module="suppliers">
            <SuppliersPage />
          </ProtectedRoute>
        }
      />

      {/* Operations Analytics by Importer/Supplier */}
      <Route
        path="/analytics/operations"
        element={
          <ProtectedRoute allowedRoles={['admin', 'ops']}>
            <OperationsByPartyPage />
          </ProtectedRoute>
        }
      />

      {/* Catalogs - admin only */}
      <Route
        path="/catalogs"
        element={
          <ProtectedRoute allowedRoles={['admin']} module="catalogs">
            <CatalogsPage />
          </ProtectedRoute>
        }
      />

      {/* Users - admin only */}
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['admin']} module="users">
            <UsersPage />
          </ProtectedRoute>
        }
      />

      {/* Organizations (Admin Master) - admin only */}
      <Route
        path="/organizations"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <OrganizationsPage />
          </ProtectedRoute>
        }
      />

      {/* Payments - Balance de Pagos - admin/ops */}
      <Route
        path="/payments"
        element={
          <ProtectedRoute allowedRoles={['admin', 'ops']} module="payments">
            <PaymentsPage />
          </ProtectedRoute>
        }
      />

      {/* Client Payments - Mi Balance - client */}
      <Route
        path="/my-payments"
        element={
          <ProtectedRoute allowedRoles={['client']} module="payments">
            <ClientPaymentsPage />
          </ProtectedRoute>
        }
      />

      {/* Arnulfo - OFIMEX internal staff (super/master) always; ORG ADMINS
          only when the `client_arnulfo` feature is enabled. ops/client see nothing. */}
      <Route
        path="/arnulfo"
        element={
          <ProtectedRoute allowedRoles={['admin']} feature="client_arnulfo">
            <ArnulfoPage />
          </ProtectedRoute>
        }
      />

      {/* Alerts - admin only */}
      <Route
        path="/alerts"
        element={
          <ProtectedRoute allowedRoles={['admin']} module="alerts">
            <AlertsPage />
          </ProtectedRoute>
        }
      />

      {/* Audit - admin only */}
      <Route
        path="/audit"
        element={
          <ProtectedRoute allowedRoles={['admin']} module="audit">
            <AuditPage />
          </ProtectedRoute>
        }
      />

      {/* Certificates - admin/ops */}
      <Route
        path="/certificates"
        element={
          <ProtectedRoute allowedRoles={['admin', 'ops']} module="certificates">
            <CertificatesPage />
          </ProtectedRoute>
        }
      />

      {/* Pricing - admin with view_prices permission */}
      <Route
        path="/pricing"
        element={
          <ProtectedRoute allowedRoles={['admin']} field="view_prices">
            <PricingPage />
          </ProtectedRoute>
        }
      />

      {/* Tracking - admin/ops */}
      <Route
        path="/tracking"
        element={
          <ProtectedRoute allowedRoles={['admin', 'ops']} module="tracking">
            <TrackingPage />
          </ProtectedRoute>
        }
      />

      {/* Email Settings - admin only */}
      <Route
        path="/email-settings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <EmailSettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['admin']} module="reports">
            <ReportsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/manuals"
        element={
          <ProtectedRoute allowedRoles={['admin', 'ops', 'client']}>
            <ManualPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/ai-arancel"
        element={
          <ProtectedRoute allowedRoles={['admin', 'ops', 'super_admin']}>
            <AIArancelPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/sla"
        element={
          <ProtectedRoute allowedRoles={['admin', 'ops']} module="sla">
            <SlaPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin"
        element={
          <ProtectedRoute requireSuperAdmin={true}>
            <SuperAdminPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/audit-logs"
        element={
          <ProtectedRoute requireSuperAdmin={true}>
            <AuditLogsPage />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route
        path="/"
        element={
          user?.role === 'client' ? (
            <Navigate to="/my-dashboard" replace />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        }
      />

      {/* 404 - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <LanguageWelcomeModal />
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
